import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTBSDropdown, {
  KTBSDropdownDirections,
} from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
  KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDateRangePicker, {
  dateFilter,
  getDateFilterOption,
} from 'general/components/Others/CustomDateRangePicker';
import ButtonFilterDropdown from 'general/components/quybka/Buttons/ButtonFilterDropdown';
import FileUploadView from 'general/components/quybka/Uploads/FileUploadView';
import AppColors from 'general/constants/AppColors';
import AppConfigs from 'general/constants/AppConfigs';
import AppResource from 'general/constants/AppResource';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import useWindowSize from 'hooks/useWindowSize';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AgencyContractEditView from '../AgencyContractEditView';
import AgencySaleDialogView from '../AgencySaleDialogView';
import AgencyContractListFilterView from './components/AgencyContractListFilterView';
import DealerHelper from 'features/ManageDealer/dealerHelper';
import useAppConfig from 'hooks/useAppConfig';
import AppConstants from 'general/constants/AppConstants';
import { SystemConstants } from 'features/System/systemHelper';
import VITableRowDropDown from 'general/components/VITableRowDropDown';
import AgencySaleCreateAppendixView from '../AgencySaleCreateAppendixView';
import AppendicesListView from '../AppendicesListView';
import DropdownTableConfigColumn from 'general/components/DropdownTableConfigColumn';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';

AgencyContractListView.propTypes = {
  agencyId: PropTypes.string,
  showFullUI: PropTypes.bool,
  prefKey: PropTypes.string,
  onRefresh: PropTypes.func,
  contractListProps: PropTypes.array,
  ignoreFilterButton: PropTypes.bool,
  ignoreCreateContractButton: PropTypes.bool,
  premiseId: PropTypes.string,
  isAddContractDone: PropTypes.bool,
};

const defaultColumnIds = [
  'CONTRACT_ID',
  'CONTRACT_SAMPLE',
  'CONTRACT_TYPE',
  'CONTRACT_NUMBER',
  'CONTRACT_DATE',
  'VALID_FROM',
  'VALID_TO',
  'POA_DOCUMENT',
  'CONTRACT_STATUS',
  'ATTACHED_FILES',
  'DATE',
  'APPENDICES',
  'AGENCY_ROLE',
  'AGENCY_NAME',
];

function AgencyContractListView({
  premiseId,
  contractListProps = [],
  agencyId = '',
  showFullUI,
  prefKey,
  onRefresh,
  ignoreFilterButton = true,
  ignoreCreateContractButton = false,
  isAddContractDone = false,
}) {
  // MARK: ---- PROPERTIES ----
  const router = useRouter();
  const { user } = useSelector((state) => state.auth);
  const app = useSelector((state) => state.app);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const isAgency = user?.role === 'AGENCY';
  const [agencyDetail, setAgencyDetail] = useState();
  const [filter, setFilter] = useState();
  const commissionRateSale = useAppConfig()?.commissionRateSale;
  const commissionRateGroup = useAppConfig()?.commissionRateGroup;
  const commissionRateLandlord = useAppConfig()?.commissionRateLandlord;
  const operatorId = useMemo(() => {
    return app?.appConfig?.operators?.[0]?.operatorId ?? null;
  }, [app]);
  useEffect(() => {
    handleGetAgencyDetail();
  }, [agencyId]);
  const handleGetAgencyDetail = () => {
    if (Utils.isValid(agencyId)) {
      AgencyHelper.API.requestAgencyDetail(agencyId).then((res) => {
        setAgencyDetail(res);
      });
    }
  };
  useEffect(() => {
    if (Utils.isValid(prefKey)) {
      setFilter(AgencyHelper.Filter.contractList);
    } else {
      setFilter({
        page: 0,
        limit: AppConfigs.defaultPageLimit,
        q: '',
        status: '',
        dateFilter: dateFilter.baMuoiNgayGanNhat,
        from: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[0].format('YYYY-MM-DD'),
        to: getDateFilterOption(dateFilter.baMuoiNgayGanNhat)[1].format('YYYY-MM-DD'),
      });
    }
  }, [prefKey]);
  const [contractList, setContractList] = useState([]);
  const [total, setTotal] = useState(0);
  useEffect(() => {
    if (!!premiseId) {
      setContractList(contractListProps);
      setTotal(contractListProps?.length);
    } else {
      if (Utils.isValid(filter)) {
        let params = _.pickBy(filter, (value) => value !== '');
        if (Utils.isValid(agencyId)) {
          params.agencyId = agencyId;
        }
        AgencyHelper.API.requestGetAgencyContractList(params).then((res) => {
          setContractList(res?.contracts);
          setTotal(res?.total);
        });
      }
    }
  }, [filter, agencyId]);
  const fullColumns = useMemo(() => {
    return [
      {
        id: 'CONTRACT_ID',
        name: UIHelper.buildTableColumn('Mã hợp đồng'),
        selector: (row) => (
          <span
            className="cursor-pointer hover-opacity-60"
            onClick={() => handleViewContract(row)}
          >
            {row?.contractCode ?? row?.contractId}
          </span>
        ),
        width: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CONTRACT_SAMPLE',
        name: UIHelper.buildTableColumn('Mẫu hợp đồng'),
        selector: (row) => AgencyHelper.Utils.getContractRoleName(row?.contractRole),
        minWidth: '220px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONTRACT_TYPE',
        name: UIHelper.buildTableColumn('Loại hợp đồng'),
        selector: (row) =>
          _.find(AgencyHelper.Data.contractTypeOptions, (item) => item.value === row?.contractType)
            ?.name,
        width: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'CONTRACT_NUMBER',
        name: UIHelper.buildTableColumn('Số hợp đồng'),
        selector: (row) => row?.contractNumber,
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AGENCY_ROLE',
        name: UIHelper.buildTableColumn('Vai trò đối tác'),
        selector: (row) => _.find(AgencyHelper.Data.agencyRoleOptions, item => item?.value === row?.agency?.primaryRole)?.name,
        minWidth: '150px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'AGENCY_NAME',
        name: UIHelper.buildTableColumn('Tên đối tác'),
        selector: (row) => (
          <KTTooltip offset={[0, 0]} text={row?.agency?.name}>
            <span
              className="cursor-pointer hover-opacity-60"
              onClick={() => {
                AgencyHelper.Navigation.navigateToAgencyDetail(null, row?.agency);
              }}
            >
              {row?.agency?.name}
            </span>
          </KTTooltip>
        ),
        minWidth: '240px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CONTRACT_DATE',
        name: UIHelper.buildTableColumn('Ngày hợp đồng'),
        selector: (row) => Utils.formatDateTimeToString(row?.contractDate, 'DD/MM/YYYY'),
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'VALID_FROM',
        name: UIHelper.buildTableColumn('Ngày hiệu lực'),
        selector: (row) => Utils.formatDateTimeToString(row?.validFrom, 'DD/MM/YYYY'),
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'VALID_TO',
        name: UIHelper.buildTableColumn('Ngày hết hạn'),
        selector: (row) => Utils.formatDateTimeToString(row?.validTo, 'DD/MM/YYYY'),
        minWidth: '140px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'POA_DOCUMENT',
        name: UIHelper.buildTableColumn('Giấy uỷ quyền'),
        selector: (row) => {
          return row?.poaDocument ? (
            <span
              className="hover-opacity-60 cursor-pointer"
              onClick={() => {
                if (Utils.isValid(row?.poaDocument))
                  Utils.openInNewTab(Utils.getFullUrl(row?.poaDocument));
              }}
            >
              Xem chi tiết
            </span>
          ) : (
            <></>
          );
        },
        minWidth: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'CONTRACT_STATUS',
        name: UIHelper.buildTableColumn('Trạng thái'),
        selector: (row) => (
          <KTLabel
            text={AgencyHelper.Utils.getContractStatusName(row?.status)}
            isInline={true}
            size={KTLabelSize.medium}
            additionalClassName={`label-light-${AgencyHelper.Utils.getContractStatusColor(
              row?.status
            )}`}
            additionalDescription={row?.cancelingReason}
          />
        ),
        width: '160px',
        style: tableStyle.columns.normal,
      },
      {
        id: 'ATTACHED_FILES',
        name: UIHelper.buildTableColumn('Tệp đính kèm'),
        selector: (row) => {
          return row?.attachedFiles?.length > 0 ? (
            <span
              className="hover-opacity-60 cursor-pointer"
              onClick={() => {
                handleViewFiles(row?.attachedFiles);
              }}
            >
              {`Xem chi tiết (${row?.attachedFiles?.length})`}
            </span>
          ) : (
            <></>
          );
        },
        minWidth: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'APPENDICES',
        name: UIHelper.buildTableColumn("Phụ lục hợp đồng"),
        selector: (row) => {
          return row?.appendices?.length > 0 ? (
            <div className='d-flex gap-1'>
              <span
                className="hover-opacity-60 cursor-pointer"
                onClick={() => {
                  handleShowAppendicesList(row?.appendices, row?.contractId);
                }}
              >
                {`Phụ lục (${row?.appendices?.length})`}
              </span>
              {row.appendices.some(item => item?.status !== 'COMPLETED') && (
                <KTTooltip text="Hợp đồng đang tồn tại phụ lục chưa được hoàn thành.">
                  <i className="fas fa-triangle-exclamation" style={{ width: 18, color: AppColors.warning }} />
                </KTTooltip>
              )}
            </div>
          ) : (
            <></>
          );
        },
        minWidth: '140px',
        style: tableStyle.columns.link,
      },
      {
        id: 'DATE',
        name: UIHelper.buildTableColumn('Ngày tạo'),
        selector: (row) => Utils.formatDateTimeToString(row?.createdAt),
        minWidth: '180px',
        style: tableStyle.columns.normal,
      },
      {
        name: UIHelper.buildTableColumn('Thao tác'),
        maxWidth: '140px',
        style: {
          position: 'sticky',
          right: 0,
          backgroundColor: '#fff',
          borderLeft: '1px solid #ebedfe',
        },
        center: false,
        cell: (row) => {
          return UIHelper.buildMoreActionsButton(buildMoreActionsContract(row), true);
        },
      },
    ];
  }, [user, agencyDetail]);
  const [fullColumnIds, setFullColumnIds] = useState(
    fullColumns.filter(column => !column.omit).map(column => column.id).filter(Boolean) || []
  );
  const {
    columnIds,
    columns,
    onChangeCheckboxValue,
    resetDefaultTableColumn,
    onSelectAll,
    onUnSelectAll,
    fullColumnsSorted,
    fullColumnsIdSorted,
    saveChanges,
  } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
  const windowSize = useWindowSize();

  useEffect(() => {
    if (isAddContractDone) {
      handleRefresh({ ...filter })
    }
  }, [isAddContractDone])

  // MARK: ---- METHODS ----
  const handleViewFiles = (files) => {
    AppCustomDialogHelper.show(
      'Tệp đính kèm',
      <FileUploadView
        pathList={files}
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
      />,
      'md',
      false,
      true
    );
  };

  const handleShowAppendicesList = (appendices, contractId) => {
    AppCustomDialogHelper.show(
      'Thông tin phụ lục hợp đồng',
      <AppendicesListView
        appendicesList={appendices}
        contractId={contractId}
        onRefresh={() => {
          handleRefresh({ ...filter })
        }}
      />,
      'xl',
      false,
      true
    )
  }

  const handleRefresh = (newFilter) => {
    if (Utils.isValid(prefKey)) {
      AgencyHelper.Filter.contractList = {
        ...AgencyHelper.Filter.contractList,
        ...newFilter,
      };
      setFilter({ ...AgencyHelper.Filter.contractList });
    } else {
      setFilter({ ...newFilter });
    }
  };
  const handleViewContract = (row) => {
    if (!Utils.isValid(row?.contractDocument)) return;

    const token = user?.token;
    let url = Utils.getFullUrl(row.contractDocument);

    if (token) {
      url += (url.includes('?') ? '&' : '?') + `token=${token}`;
    }

    Utils.openInNewTab(url);
  }
  const buildMoreActionsContract = (row) => {
    const actions = [];
    // xem chi tiet
    actions.push({
      text: 'Xem chi tiết',
      iconElement: <i className="fa-regular fa-eye" style={{ fontSize: '1.1rem' }} />,
      click: () => handleViewContract(row),
    });
    if (isAdmin) {
      if (row?.status === AgencyConstants.ContractStatus.CREATED) {
        actions.push({
          text: 'Ký hợp đồng',
          iconElement: (
            <i className="fa-regular fa-file-signature" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            handleSignContract(row);
          },
        });
      }
      if (
        row?.contractRole &&
        ![AgencyConstants.ContractRole.SALE, AgencyConstants.ContractRole.FRANCHISE].includes(row.contractRole)
      ) {
        actions.push({
          text: 'Xem đơn hàng liên quan',
          iconElement: (
            <i className="fa-regular fa-memo-circle-info" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            router.navigate(`sale/${row?.saleId}`);
          },
        });
      }
      if (row?.contractType === 'DIGITAL' && row?.status === 'CREATED' || row?.contractType === 'PAPER') {
        actions.push({
          text: 'Chỉnh sửa',
          iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            if (Utils.isValid(row?.saleId)) {
              AppCustomDialogHelper.show(
                'Chỉnh sửa hợp đồng',
                <AgencyContractEditView
                  contractDetail={row}
                  onCancel={() => {
                    AppCustomDialogHelper.hide();
                  }}
                  onConfirm={() => {
                    AppCustomDialogHelper.hide();
                    setFilter({ ...filter });
                  }}
                />,
                'md',
                false,
                true
              );
            } else {
              AgencyHelper.UI.showDialogConfirmCreateContractWithAgency(
                agencyDetail,
                () => {
                  if (onRefresh) onRefresh();
                  handleRefresh({ ...filter });
                },
                row
              );
            }
          },
        });
      }
      if (row?.status === AgencyConstants.ContractStatus.COMPLETED) {
        actions.push({
          text: 'Tạo phụ lục hợp đồng',
          iconElement: <i className="fa-regular fa-plus" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            AppCustomDialogHelper.show(
              '',
              <AgencySaleCreateAppendixView
                agencyDetail={agencyDetail}
                contract={row}
                title={"Tạo phụ lục hợp đồng"}
                appendicesList={row?.appendices}
                contentEl={
                  <>
                    Bạn muốn tạo phụ lục hợp đồng cho{' '}
                    <span>
                      {AgencyHelper.Utils.getContractRoleName(row?.contractRole).toLocaleLowerCase()}
                    </span>
                    {' '} số {' '}
                    <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                      {row?.contractNumber}
                    </span>
                    ?
                  </>
                }
                onCancel={() => {
                  AppCustomDialogHelper.hide();
                }}
                onConfirm={async (param) => {
                  let currentAppendices = row?.appendices || [];
                  const updatedAppendices = Array.isArray(param)
                    ? [...currentAppendices, ...param]
                    : [...currentAppendices, param];
                  const result = await AgencyHelper.API.requestUpdateContract(
                    row?.contractId,
                    {
                      appendices: updatedAppendices
                    }
                  );
                  if (_.isObject(result)) {
                    ToastHelper.showSuccess('Tạo phụ lục hợp đồng thành công');
                    handleRefresh(filter);
                    AppCustomDialogHelper.hide();
                  } else {
                    ToastHelper.showError(result || 'Tạo phụ lục hợp đồng không thành công');
                  }
                  return false;
                }}
              />,
              'lg',
              false,
              true
            );
          },
        });
      }
      if (row?.status !== AgencyConstants.ContractStatus.CANCELLED) {
        actions.push({
          text: 'Hủy hợp đồng',
          iconElement: <i className="fa-regular fa-circle-xmark" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            AppCustomDialogHelper.show(
              '',
              <AgencySaleDialogView
                iconEl={<img src={AppResource.images.dialogIcons.dialogError} />}
                title="Hủy hợp đồng"
                contentEl={
                  <>
                    Nhập lý do hủy hợp đồng {' '}
                    <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                      {row?.contractNumber}
                    </span>
                  </>
                }
                inputLabel={['Lý do']}
                showInput={true}
                onCancel={() => {
                  AppCustomDialogHelper.hide();
                }}
                onConfirm={async (values) => {
                  const [cancelingReason] = values;
                  AppCustomDialogHelper.hide();
                  const result = await AgencyHelper.API.requestUpdateContract(row?.contractId, {
                    status: AgencyConstants.ContractStatus.CANCELLED,
                    cancelingReason: cancelingReason,
                  });
                  let updateOrderResult = true;
                  if (!!row?.sale) {
                    updateOrderResult = await AgencyHelper.API.requestUpdateSale(row?.sale?.saleId, {
                      status: AgencyConstants.SaleOrderStatus.CANCELLED,
                    });
                  }
                  if (result && _.isObject(result) && updateOrderResult && _.isBoolean(updateOrderResult)) {
                    ToastHelper.showSuccess('Hủy hợp đồng thành công');
                    setFilter({ ...filter });
                  } else {
                    ToastHelper.showError(result || 'Hủy hợp đồng thất bại');
                  }
                }}
                confirmButtonRed={true}
              />,
              'md',
              false,
              true
            );
          },
        });
      }
      if (row?.status === 'CREATED') {
        actions.push({
          text: 'Xoá hợp đồng',
          iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
          click: () => {
            AppCustomDialogHelper.show(
              '',
              <AgencySaleDialogView
                iconEl={<img src={AppResource.images.dialogIcons.dialogError} />}
                title="Xoá hợp đồng"
                contentEl={
                  <>
                    Bạn có chắc muốn xoá hợp đồng{' '}
                    <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
                      {row?.contractNumber}
                    </span>
                    ?
                  </>
                }
                onCancel={() => {
                  AppCustomDialogHelper.hide();
                }}
                onConfirm={async () => {
                  AppCustomDialogHelper.hide();
                  const result = await AgencyHelper.API.requestDeleteContract(row?.contractId);
                  if (result && _.isBoolean(result)) {
                    ToastHelper.showSuccess('Xoá hợp đồng thành công');
                    setFilter({ ...filter });
                  } else {
                    ToastHelper.showError(result || 'Xoá hợp đồng thất bại');
                  }
                }}
                confirmButtonRed={true}
              />,
              'md',
              false,
              true
            );
          },
        });
      }
    } else if (isAgency) {
      if (row?.status === AgencyConstants.ContractStatus.ADMIN_SIGNED) {
        actions.push({
          text: 'Ký hợp đồng',
          iconElement: (
            <i className="fa-regular fa-file-signature" style={{ fontSize: '1.1rem' }} />
          ),
          click: () => {
            handleSignContract(row);
          },
        });
      }
    }
    return actions;
  };
  const handleSignContract = (row) => {
    AppCustomDialogHelper.show(
      '',
      <AgencySaleDialogView
        iconEl={<img src={AppResource.images.dialogIcons.dialogSignContract} />}
        title="Ký hợp đồng"
        contentEl={
          <>
            Bạn muốn ký hợp đồng:{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray[600])}>
              {row?.contractNumber}
            </span>
            ?
          </>
        }
        showInput={!isAdmin}
        inputLabel={['Định danh chữ ký số (UUID)', 'Nhập mật khẩu']}
        inputType={['text', 'password']}
        showAdditionalInfo={!isAdmin}
        additionalInfo={
          <div
            className="d-flex gap-3 p-3 align-items-center"
            style={{
              border: `1px dashed ${AppColors.danger}`,
              backgroundColor: '#FFF6F6',
            }}
          >
            <div className="w-32px h-32px rounded-circle bg-danger-o-20 d-flex align-items-center justify-content-center">
              <i className="fas fa-signature" style={{ color: AppColors.danger }} />
            </div>
            <div className="d-flex flex-column">
              <div style={UIHelper.buildFontStyle('700', '20px', AppColors.danger)}>
                Bạn chưa có chữ ký số?
              </div>
              <div>
                <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>
                  Vui lòng tạo chữ ký số{' '}
                </span>
                <a
                  href={'#'}
                  target={'_blank'}
                  style={UIHelper.buildFontStyle('700', '14px', AppColors.danger)}
                >
                  tại đây
                </a>
              </div>
            </div>
          </div>
        }
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
        onConfirm={async (values) => {
          let contract = null;
          if (isAdmin) {
            contract = await AgencyHelper.API.requestAdminSignContract(row?.contractId);
          } else {
            const [hsmAgreementUUID, hsmPassword] = values;
            contract = await AgencyHelper.API.requestAgencySignContract(
              row?.contractId,
              hsmAgreementUUID,
              hsmPassword
            );
          }
          if (contract && _.isObject(contract)) {
            AppCustomDialogHelper.hide();
            ToastHelper.showSuccess('Ký hợp đồng thành công');
            setFilter({ ...filter });
          } else {
            ToastHelper.showError(contract || 'Ký hợp đồng thất bại');
          }
        }}
      />,
      'md',
      false,
      true
    );
  };

  const handleCreateSaleCommission = async () => {
    await DealerHelper.callApiCreateAgencyCommission(
      Utils.transformEmptyStrings({
        operatorId: operatorId,
        agencyId: agencyDetail?.agencyId,
        role: 'SALE',
        energyCommissionMode: AppConstants.energyCommissionMode.fixedPerKwh,
        energyCommissionValue: 0,
        energyCommissionDuration: null,
        serviceCommission: 0,
        saleCommission: [
          {
            revenue: 0,
            discount: Number(commissionRateSale),
          },
        ],
        packageCommission: [
          {
            revenue: 0,
            discount: Number(commissionRateGroup),
          },
        ],
        landlordCommission: JSON.parse(commissionRateLandlord),
      })
    );
    handleGetAgencyDetail();
  };

  return (
    <div>
      {UIHelper.buildCard(
        <div>
          {/* header */}
          {showFullUI && (
            <div className="px-4 pt-4 d-flex justify-content-between align-items-center">
              {/* title */}
              <div className="d-flex flex-column align-items-start">
                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                  Danh sách hợp đồng
                </span>
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  Tổng số:{' '}
                  <span style={{ color: AppColors.primary }}>
                    {Utils.formatNumber(total)} hợp đồng
                  </span>
                </span>
              </div>

              {/* actions */}
            </div>
          )}

          {/* body */}
          <div className="">
            {/* filter */}
            <div className="row m-0 p-0 px-2">
              {/* tim kiem */}
              <div className="col mb-4 px-2">
                {UIHelper.buildFormLabel('Tìm kiếm')}
                <KTFormSearch
                  name="search"
                  placeholder={!!prefKey ? "Theo mã hợp đồng, số hợp đồng, tên đối tác" : "Theo mã hợp đồng, số hợp đồng"}
                  value={filter?.q}
                  onSubmit={(text) => {
                    handleRefresh({ ...filter, q: text, page: 0 });
                  }}
                />
              </div>

              {!prefKey ? (
                <>
                  {/* loc theo trang thai */}
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Trạng thái')}
                    <KTBSDropdown
                      name="status"
                      toggleElement={
                        <button
                          className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                          type="button"
                        >
                          {_.find(AgencyHelper.Data.contractOptions, { value: filter?.status })
                            ?.name ?? 'Tất cả'}
                        </button>
                      }
                      selectedValue={filter?.status}
                      dropdownMenuItems={[
                        { name: 'Tất cả', value: '' },
                        ...AgencyHelper.Data.contractOptions,
                      ]}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={(value) => {
                        handleRefresh({ ...filter, status: value, page: 0 });
                      }}
                      isDropdownForm={true}
                      direction={(showFullUI && !agencyId) ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                    />
                  </div>

                  {/* loc theo khoang thoi gian */}
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Khoảng thời gian')}
                    <CustomDateRangePicker
                      alignRight={true}
                      toggleFontWeight="400"
                      toggleElementBorderRadius={5}
                      initialValue={filter?.dateFilter}
                      onPressSave={(selectedValue, startDate, endDate) => {
                        handleRefresh({
                          ...filter,
                          dateFilter: selectedValue,
                          from: startDate,
                          to: endDate,
                          page: 0,
                        });
                      }}
                      direction={(showFullUI && !agencyId) ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* loc theo mau hop dong */}
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Mẫu hợp đồng')}
                    <KTBSDropdown
                      name="contractRole"
                      toggleElement={
                        <button
                          className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                          type="button"
                        >
                          {_.find(AgencyHelper.Data.contractRoleOptions, { value: filter?.contractRole })
                            ?.name ?? 'Tất cả'}
                        </button>
                      }
                      selectedValue={filter?.contractRole}
                      dropdownMenuItems={[
                        { name: 'Tất cả', value: '' },
                        ...AgencyHelper.Data.contractRoleOptions,
                      ]}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={(value) => {
                        handleRefresh({ ...filter, contractRole: value, page: 0 });
                      }}
                      isDropdownForm={true}
                      direction={(showFullUI && !agencyId) ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                    />
                  </div>
                  {/* loc theo loai hop dong */}
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Loại hợp đồng')}
                    <KTBSDropdown
                      name="contractType"
                      toggleElement={
                        <button
                          className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                          type="button"
                        >
                          {_.find(AgencyHelper.Data.contractTypeOptions, { value: filter?.contractType })
                            ?.name ?? 'Tất cả'}
                        </button>
                      }
                      selectedValue={filter?.contractType}
                      dropdownMenuItems={[
                        { name: 'Tất cả', value: '' },
                        ...AgencyHelper.Data.contractTypeOptions,
                      ]}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={(value) => {
                        handleRefresh({ ...filter, contractType: value, page: 0 });
                      }}
                      isDropdownForm={true}
                      direction={(showFullUI && !agencyId) ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                    />
                  </div>
                  {/* loc theo vai tro doi tac */}
                  <div className="col mb-4 px-2">
                    {UIHelper.buildFormLabel('Vai trò đối tác')}
                    <KTBSDropdown
                      name="primaryRole"
                      toggleElement={
                        <button
                          className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
                          type="button"
                        >
                          {_.find(AgencyHelper.Data.agencyRoleOptions, { value: filter?.primaryRole })
                            ?.name ?? 'Tất cả'}
                        </button>
                      }
                      selectedValue={filter?.primaryRole}
                      dropdownMenuItems={[
                        { name: 'Tất cả', value: '' },
                        ...AgencyHelper.Data.agencyRoleOptions,
                      ]}
                      dropdownMenuClassName="overflow-auto w-100"
                      dropdownMenuStyle={{ maxHeight: '30vh' }}
                      onChange={(value) => {
                        handleRefresh({ ...filter, primaryRole: value, page: 0 });
                      }}
                      isDropdownForm={true}
                      direction={(showFullUI && !agencyId) ? KTBSDropdownDirections.down : KTBSDropdownDirections.up}
                    />
                  </div>
                </>
              )}

              <div className="col mb-4 px-2 d-flex align-items-end gap-3">
                {!ignoreFilterButton && (
                  <ButtonFilterDropdown
                    dropdownContent={
                      <AgencyContractListFilterView
                        onCancel={(newFilter) => {
                          handleRefresh(newFilter);
                        }}
                        onApply={(newFilter) => {
                          handleRefresh(newFilter);
                        }}
                        initialFilter={filter}
                      />
                    }
                  />
                )}
                {/* cau hinh cot */}
                {!!prefKey && (
                  <DropdownTableConfigColumn
                    columnIds={columnIds}
                    fullColumns={fullColumns}
                    fullColumnIds={fullColumnsIdSorted}
                    onChangeCheckboxValue={onChangeCheckboxValue}
                    saveChanges={saveChanges}
                    resetDefaultTableColumn={() => {
                      setFullColumnIds(defaultColumnIds);
                      resetDefaultTableColumn();
                    }}
                    onChangeFullColumnIds={(ids) => {
                      setFullColumnIds(ids);
                      const key = prefKey + '_FULL_COLUMN_IDS';
                      localStorage.setItem(key, ids.join('|'));
                    }}
                    onSelectAll={onSelectAll}
                    onUnSelectAll={onUnSelectAll}
                  />
                )}
                {!ignoreCreateContractButton && (
                  <>
                    {isAdmin &&
                      agencyDetail?.status !== SystemConstants.UserStatus.REGISTERED && (
                        <div>
                          {
                            agencyDetail?.primaryRole !== AgencyConstants.AgencyRole.INVESTOR
                              ? UIHelper.buildGreenButton(
                                'Tạo hợp đồng CTV',
                                () => {
                                  AgencyHelper.UI.showDialogConfirmCreateContractWithAgency(
                                    agencyDetail,
                                    () => {
                                      if (onRefresh) onRefresh();
                                      handleRefresh({ ...filter });
                                    },
                                    undefined,
                                    async () => {
                                      const hasSaleCommission =
                                        agencyDetail && agencyDetail.commissions
                                          ? await agencyDetail.commissions.some(
                                            (commission) => commission?.role === 'SALE'
                                          )
                                          : false;

                                      if (!hasSaleCommission) {
                                        await handleCreateSaleCommission();
                                      }
                                    }
                                  );
                                },
                                <i className="fa-regular fa-circle-plus" />
                              )
                              : <VITableRowDropDown
                                toggleElement={
                                  UIHelper.buildGreenButton(
                                    <>
                                      Tạo hợp đồng <i className="fa-solid fa-caret-down ml-2" />
                                    </>,
                                    () => { },
                                    <i className="fa-regular fa-circle-plus" />
                                  )
                                }
                                menuItem={[
                                  {
                                    iconElement: <i className={`fa-regular fa-file-contract`}></i>,
                                    text: 'Tạo hợp đồng nhượng quyền',
                                    click: () => {
                                      AgencyHelper.UI.showDialogConfirmCreateContractWithAgency(
                                        agencyDetail,
                                        () => {
                                          if (onRefresh) onRefresh();
                                          handleRefresh({ ...filter });
                                        },
                                        undefined,
                                        null,
                                        AgencyConstants.ContractRole.FRANCHISE,
                                      );
                                    },
                                  },
                                  {
                                    iconElement: <i className={`fa-regular fa-file-contract`}></i>,
                                    text: 'Tạo hợp đồng cộng tác viên',
                                    click: () => {
                                      AgencyHelper.UI.showDialogConfirmCreateContractWithAgency(
                                        agencyDetail,
                                        () => {
                                          if (onRefresh) onRefresh();
                                          handleRefresh({ ...filter });
                                        },
                                        undefined,
                                        async () => {
                                          const hasSaleCommission =
                                            agencyDetail && agencyDetail.commissions
                                              ? await agencyDetail.commissions.some(
                                                (commission) => commission?.role === 'SALE'
                                              )
                                              : false;

                                          if (!hasSaleCommission) {
                                            await handleCreateSaleCommission();
                                          }
                                        },
                                      );
                                    },
                                  },
                                ]}
                                singleBtnBgColor={AppColors.gray[200]}
                              />
                          }
                        </div>
                      )
                    }
                  </>
                )}
              </div>
            </div>

            {/* table */}
            <div className="px-4">
              <KTTable
                columns={columns}
                items={contractList}
                selectableRows={false}
                striped={false}
                compactGrid={false}
                onClickedRow={(row) => { }}
                onSelectedRowsChanged={(rows) => { }}
                onDoubleClickedRow={(row) => { }}
                noDataComponent={UIHelper.buildEmptyNoData(
                  'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                )}
              />
            </div>

            {/* phan trang */}
            <div className={showFullUI ? 'my-4 px-4' : 'mt-4 px-4'}>
              <KTPagination
                currentPage={filter?.page + 1}
                rowCount={total}
                rowsPerPage={filter?.limit}
                currentItemInPageCount={contractList.length}
                onChangePage={(newPage) => {
                  const iNewPage = parseInt(newPage);
                  handleRefresh({
                    ...filter,
                    page: iNewPage - 1,
                  });
                }}
                onChangeRowsPerPage={(newRowsPerPage) => {
                  const iNewRowsPerPage = parseInt(newRowsPerPage);
                  handleRefresh({
                    ...filter,
                    limit: iNewRowsPerPage,
                    page: 0,
                  });
                }}
                loading={false}
                color={KTPaginationColor.success}
                appendText="hợp đồng"
              />
            </div>
          </div>
        </div>,
        false,
        !showFullUI
      )
      }
    </div >
  );
}

export default AgencyContractListView;
